import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Scale, LogOut, Users, FileText, Inbox, Plus, 
  Eye, Edit, Calendar, Phone, Mail, Briefcase 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [leads, setLeads] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [processos, setProcessos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNovoCliente, setShowNovoCliente] = useState(false);
  const [showNovoProcesso, setShowNovoProcesso] = useState(false);

  const [novoCliente, setNovoCliente] = useState({
    nome: '',
    email: '',
    telefone: '',
    cpf: '',
    endereco: '',
    senha: ''
  });

  const [novoProcesso, setNovoProcesso] = useState({
    cliente_id: '',
    numero_processo: '',
    tipo: '',
    descricao: '',
    status: 'em andamento',
    valor_causa: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('userData');
    
    if (!token) {
      navigate('/admin/login');
      return;
    }

    if (user) {
      setUserData(JSON.parse(user));
    }

    carregarDados();
  }, [navigate]);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const carregarDados = async () => {
    setLoading(true);
    try {
      const [leadsRes, clientesRes, processosRes] = await Promise.all([
        axios.get(`${API}/admin/leads`, getAuthHeader()),
        axios.get(`${API}/admin/clientes`, getAuthHeader()),
        axios.get(`${API}/admin/processos`, getAuthHeader())
      ]);

      setLeads(leadsRes.data);
      setClientes(clientesRes.data);
      setProcessos(processosRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/admin/login');
  };

  const handleCriarCliente = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/clientes`, novoCliente, getAuthHeader());
      toast.success('Cliente criado com sucesso!');
      setShowNovoCliente(false);
      setNovoCliente({
        nome: '',
        email: '',
        telefone: '',
        cpf: '',
        endereco: '',
        senha: ''
      });
      carregarDados();
    } catch (error) {
      toast.error('Erro ao criar cliente');
      console.error(error);
    }
  };

  const handleCriarProcesso = async (e) => {
    e.preventDefault();
    try {
      const dados = {
        ...novoProcesso,
        valor_causa: novoProcesso.valor_causa ? parseFloat(novoProcesso.valor_causa) : null
      };
      await axios.post(`${API}/admin/processos`, dados, getAuthHeader());
      toast.success('Processo criado com sucesso!');
      setShowNovoProcesso(false);
      setNovoProcesso({
        cliente_id: '',
        numero_processo: '',
        tipo: '',
        descricao: '',
        status: 'em andamento',
        valor_causa: ''
      });
      carregarDados();
    } catch (error) {
      toast.error('Erro ao criar processo');
      console.error(error);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'novo': 'bg-blue-500',
      'contatado': 'bg-yellow-500',
      'convertido': 'bg-green-500',
      'em andamento': 'bg-blue-500',
      'concluído': 'bg-green-500',
      'aguardando': 'bg-yellow-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Scale className="w-8 h-8 text-gold" />
              <div>
                <h1 className="font-serif text-xl font-bold text-navy">
                  Painel Administrativo
                </h1>
                <p className="text-sm text-gray-600">
                  Bem-vindo, {userData?.username}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => navigate('/')}
                className="border-gold text-navy hover:bg-gold hover:text-white"
              >
                Ver Site
              </Button>
              <Button 
                variant="outline" 
                onClick={handleLogout}
                className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="card-elegant">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 mb-1">Leads Capturados</p>
                  <p className="text-3xl font-bold text-navy">{leads.length}</p>
                </div>
                <Inbox className="w-12 h-12 text-gold" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-elegant">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 mb-1">Clientes Ativos</p>
                  <p className="text-3xl font-bold text-navy">{clientes.length}</p>
                </div>
                <Users className="w-12 h-12 text-gold" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-elegant">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 mb-1">Processos</p>
                  <p className="text-3xl font-bold text-navy">{processos.length}</p>
                </div>
                <FileText className="w-12 h-12 text-gold" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="leads" className="space-y-6">
          <TabsList className="bg-white border border-gray-200">
            <TabsTrigger value="leads" data-testid="tab-leads">
              <Inbox className="w-4 h-4 mr-2" />
              Leads ({leads.length})
            </TabsTrigger>
            <TabsTrigger value="clientes" data-testid="tab-clientes">
              <Users className="w-4 h-4 mr-2" />
              Clientes ({clientes.length})
            </TabsTrigger>
            <TabsTrigger value="processos" data-testid="tab-processos">
              <FileText className="w-4 h-4 mr-2" />
              Processos ({processos.length})
            </TabsTrigger>
          </TabsList>

          {/* Leads Tab */}
          <TabsContent value="leads">
            <Card>
              <CardHeader>
                <CardTitle className="text-navy">Leads Capturados</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-center text-gray-600 py-8">Carregando...</p>
                ) : leads.length === 0 ? (
                  <p className="text-center text-gray-600 py-8">Nenhum lead capturado ainda</p>
                ) : (
                  <div className="space-y-4">
                    {leads.map((lead) => (
                      <Card key={lead.id} className="border-l-4 border-gold">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h3 className="font-semibold text-navy text-lg">{lead.nome}</h3>
                              <div className="flex gap-4 text-sm text-gray-600 mt-1">
                                <span className="flex items-center gap-1">
                                  <Mail className="w-4 h-4" />
                                  {lead.email}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Phone className="w-4 h-4" />
                                  {lead.telefone}
                                </span>
                              </div>
                            </div>
                            <Badge className={`${getStatusBadge(lead.status)} text-white`}>
                              {lead.status}
                            </Badge>
                          </div>
                          <p className="text-gray-700 mt-3">{lead.mensagem}</p>
                          <p className="text-xs text-gray-500 mt-2">
                            {new Date(lead.created_at).toLocaleString('pt-BR')}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Clientes Tab */}
          <TabsContent value="clientes">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-navy">Clientes</CardTitle>
                  <Dialog open={showNovoCliente} onOpenChange={setShowNovoCliente}>
                    <DialogTrigger asChild>
                      <Button className="btn-gold" data-testid="novo-cliente-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Novo Cliente
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Criar Novo Cliente</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCriarCliente} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold mb-2">Nome Completo *</label>
                            <Input
                              value={novoCliente.nome}
                              onChange={(e) => setNovoCliente({...novoCliente, nome: e.target.value})}
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2">CPF *</label>
                            <Input
                              value={novoCliente.cpf}
                              onChange={(e) => setNovoCliente({...novoCliente, cpf: e.target.value})}
                              required
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold mb-2">Email *</label>
                            <Input
                              type="email"
                              value={novoCliente.email}
                              onChange={(e) => setNovoCliente({...novoCliente, email: e.target.value})}
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2">Telefone *</label>
                            <Input
                              value={novoCliente.telefone}
                              onChange={(e) => setNovoCliente({...novoCliente, telefone: e.target.value})}
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold mb-2">Endereço</label>
                          <Input
                            value={novoCliente.endereco}
                            onChange={(e) => setNovoCliente({...novoCliente, endereco: e.target.value})}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold mb-2">Senha *</label>
                          <Input
                            type="password"
                            value={novoCliente.senha}
                            onChange={(e) => setNovoCliente({...novoCliente, senha: e.target.value})}
                            required
                            placeholder="Senha para acesso do cliente"
                          />
                        </div>
                        <Button type="submit" className="btn-gold w-full">
                          Criar Cliente
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-center text-gray-600 py-8">Carregando...</p>
                ) : clientes.length === 0 ? (
                  <p className="text-center text-gray-600 py-8">Nenhum cliente cadastrado</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {clientes.map((cliente) => (
                      <Card key={cliente.id} className="border-l-4 border-gold">
                        <CardContent className="p-4">
                          <h3 className="font-semibold text-navy text-lg mb-2">{cliente.nome}</h3>
                          <div className="space-y-1 text-sm text-gray-600">
                            <p className="flex items-center gap-2">
                              <Mail className="w-4 h-4" />
                              {cliente.email}
                            </p>
                            <p className="flex items-center gap-2">
                              <Phone className="w-4 h-4" />
                              {cliente.telefone}
                            </p>
                            <p className="text-xs text-gray-500 mt-2">
                              CPF: {cliente.cpf}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Processos Tab */}
          <TabsContent value="processos">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-navy">Processos</CardTitle>
                  <Dialog open={showNovoProcesso} onOpenChange={setShowNovoProcesso}>
                    <DialogTrigger asChild>
                      <Button className="btn-gold" data-testid="novo-processo-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Novo Processo
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Criar Novo Processo</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCriarProcesso} className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold mb-2">Cliente *</label>
                          <Select
                            value={novoProcesso.cliente_id}
                            onValueChange={(value) => setNovoProcesso({...novoProcesso, cliente_id: value})}
                            required
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o cliente" />
                            </SelectTrigger>
                            <SelectContent>
                              {clientes.map((cliente) => (
                                <SelectItem key={cliente.id} value={cliente.id}>
                                  {cliente.nome} - {cliente.email}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold mb-2">Número do Processo *</label>
                            <Input
                              value={novoProcesso.numero_processo}
                              onChange={(e) => setNovoProcesso({...novoProcesso, numero_processo: e.target.value})}
                              required
                              placeholder="0000000-00.0000.0.00.0000"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2">Tipo *</label>
                            <Select
                              value={novoProcesso.tipo}
                              onValueChange={(value) => setNovoProcesso({...novoProcesso, tipo: value})}
                              required
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione o tipo" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Rescisão Indireta">Rescisão Indireta</SelectItem>
                                <SelectItem value="Horas Extras">Horas Extras</SelectItem>
                                <SelectItem value="Danos Morais">Danos Morais</SelectItem>
                                <SelectItem value="Insalubridade">Insalubridade</SelectItem>
                                <SelectItem value="Cálculo de Rescisão">Cálculo de Rescisão</SelectItem>
                                <SelectItem value="Outros">Outros</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold mb-2">Descrição *</label>
                          <Textarea
                            value={novoProcesso.descricao}
                            onChange={(e) => setNovoProcesso({...novoProcesso, descricao: e.target.value})}
                            required
                            rows={4}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold mb-2">Status *</label>
                            <Select
                              value={novoProcesso.status}
                              onValueChange={(value) => setNovoProcesso({...novoProcesso, status: value})}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="em andamento">Em Andamento</SelectItem>
                                <SelectItem value="aguardando">Aguardando</SelectItem>
                                <SelectItem value="concluído">Concluído</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2">Valor da Causa (R$)</label>
                            <Input
                              type="number"
                              step="0.01"
                              value={novoProcesso.valor_causa}
                              onChange={(e) => setNovoProcesso({...novoProcesso, valor_causa: e.target.value})}
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                        <Button type="submit" className="btn-gold w-full">
                          Criar Processo
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-center text-gray-600 py-8">Carregando...</p>
                ) : processos.length === 0 ? (
                  <p className="text-center text-gray-600 py-8">Nenhum processo cadastrado</p>
                ) : (
                  <div className="space-y-4">
                    {processos.map((processo) => (
                      <Card key={processo.id} className="border-l-4 border-gold">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h3 className="font-semibold text-navy text-lg">{processo.numero_processo}</h3>
                              <Badge className="mt-1 bg-navy text-white">{processo.tipo}</Badge>
                            </div>
                            <Badge className={`${getStatusBadge(processo.status)} text-white`}>
                              {processo.status}
                            </Badge>
                          </div>
                          <p className="text-gray-700 mb-2">{processo.descricao}</p>
                          {processo.valor_causa && (
                            <p className="text-sm text-gray-600">
                              <span className="font-semibold">Valor da causa:</span> R$ {processo.valor_causa.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-2">
                            Última atualização: {new Date(processo.ultima_atualizacao).toLocaleString('pt-BR')}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
